/**
 * Industry Benchmark Comparison Component
 * Shows COGS and expense ratios compared to industry averages
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Minus, HelpCircle, CheckCircle, AlertTriangle } from 'lucide-react';

interface BenchmarkData {
  yourValue: number;
  industryAvg: number;
  industryRange: { min: number; max: number };
  label: string;
  description: string;
}

interface IndustryBenchmarkProps {
  businessType?: string;
  naicsCode?: string;
  cogsRatio?: number;
  grossMargin?: number;
  operatingExpenseRatio?: number;
  netProfitMargin?: number;
  hasReconstructedData?: boolean;
}

// Info tooltip
const InfoTooltip = ({ text }: { text: string }) => (
  <div className="group relative inline-block ml-1">
    <HelpCircle className="w-4 h-4 cursor-help inline" style={{ color: 'var(--color-text-dim)' }} />
    <div className="invisible group-hover:visible absolute z-50 w-64 p-2 text-xs border rounded-lg shadow-lg -left-28 top-6" style={{ color: 'var(--color-text-secondary)', backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {text}
      <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 border-l border-t rotate-45" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}></div>
    </div>
  </div>
);

// Industry benchmark defaults (can be replaced with API data)
const getIndustryBenchmarks = (businessType?: string): Record<string, { avg: number; min: number; max: number }> => {
  // Default benchmarks by business type - would typically come from BLS API
  const benchmarksByType: Record<string, Record<string, { avg: number; min: number; max: number }>> = {
    restaurant: {
      cogs: { avg: 32, min: 25, max: 40 },
      grossMargin: { avg: 68, min: 60, max: 75 },
      operatingExpense: { avg: 55, min: 45, max: 65 },
      netProfit: { avg: 8, min: 3, max: 15 }
    },
    retail: {
      cogs: { avg: 60, min: 50, max: 70 },
      grossMargin: { avg: 40, min: 30, max: 50 },
      operatingExpense: { avg: 30, min: 20, max: 40 },
      netProfit: { avg: 5, min: 2, max: 10 }
    },
    service: {
      cogs: { avg: 20, min: 10, max: 30 },
      grossMargin: { avg: 80, min: 70, max: 90 },
      operatingExpense: { avg: 60, min: 50, max: 70 },
      netProfit: { avg: 15, min: 8, max: 25 }
    },
    default: {
      cogs: { avg: 40, min: 25, max: 55 },
      grossMargin: { avg: 60, min: 45, max: 75 },
      operatingExpense: { avg: 45, min: 35, max: 55 },
      netProfit: { avg: 10, min: 5, max: 18 }
    }
  };

  const typeKey = businessType?.toLowerCase() || 'default';
  return benchmarksByType[typeKey] || benchmarksByType.default;
};

function BenchmarkBar({ data }: { data: BenchmarkData }) {
  const { yourValue, industryAvg, industryRange, label, description } = data;
  
  // Calculate position percentages
  const maxValue = Math.max(yourValue, industryRange.max) * 1.2;
  const yourPosition = (yourValue / maxValue) * 100;
  const avgPosition = (industryAvg / maxValue) * 100;
  const rangeStart = (industryRange.min / maxValue) * 100;
  const rangeEnd = (industryRange.max / maxValue) * 100;
  
  // Determine status
  const isWithinRange = yourValue >= industryRange.min && yourValue <= industryRange.max;
  const diff = yourValue - industryAvg;
  const diffPercent = ((diff / industryAvg) * 100).toFixed(1);
  
  const getStatus = () => {
    if (isWithinRange) return { color: 'green', icon: CheckCircle, text: 'Within range' };
    if (yourValue < industryRange.min) return { color: 'amber', icon: AlertTriangle, text: 'Below range' };
    return { color: 'amber', icon: AlertTriangle, text: 'Above range' };
  };
  
  const status = getStatus();
  
  return (
    <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{label}</span>
          <InfoTooltip text={description} />
        </div>
        <div className="flex items-center gap-1 text-xs font-medium" style={{ color: status.color === 'green' ? 'var(--color-verified)' : 'var(--color-brand)' }}>
          <status.icon className="w-3.5 h-3.5" />
          {status.text}
        </div>
      </div>

      {/* Bar visualization */}
      <div className="relative h-8 rounded-full overflow-hidden mb-2" style={{ backgroundColor: 'var(--color-bg-hover)' }}>
        {/* Industry range band */}
        <div
          className="absolute h-full"
          style={{ left: `${rangeStart}%`, width: `${rangeEnd - rangeStart}%`, backgroundColor: 'var(--color-needs-human-tint)' }}
        />

        {/* Industry average marker */}
        <div
          className="absolute w-0.5 h-full"
          style={{ left: `${avgPosition}%`, backgroundColor: 'var(--color-brand)' }}
        />

        {/* Your value marker */}
        <div
          className="absolute w-3 h-full rounded-full"
          style={{ left: `${Math.min(yourPosition, 97)}%`, backgroundColor: isWithinRange ? 'var(--color-verified)' : 'var(--color-brand)' }}
        />
      </div>

      {/* Values */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-4">
          <span style={{ color: 'var(--color-text-secondary)' }}>
            Your business: <strong style={{ color: isWithinRange ? 'var(--color-verified)' : 'var(--color-brand)' }}>{yourValue.toFixed(1)}%</strong>
          </span>
          <span style={{ color: 'var(--color-text-muted)' }}>
            Industry avg: <strong style={{ color: 'var(--color-needs-human-text)' }}>{industryAvg}%</strong>
          </span>
        </div>
        <span className="font-medium" style={{ color: diff > 0 ? 'var(--color-verified)' : diff < 0 ? 'var(--color-needs-human-text)' : 'var(--color-text-secondary)' }}>
          {diff > 0 ? '+' : ''}{diffPercent}% vs avg
        </span>
      </div>

      {/* Range indicator */}
      <div className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
        Industry range: {industryRange.min}% - {industryRange.max}%
      </div>
    </div>
  );
}

export default function IndustryBenchmark({
  businessType,
  naicsCode,
  cogsRatio = 35,
  grossMargin = 65,
  operatingExpenseRatio = 50,
  netProfitMargin = 10,
  hasReconstructedData = false
}: IndustryBenchmarkProps) {
  const [expanded, setExpanded] = useState(true);
  
  const benchmarks = getIndustryBenchmarks(businessType);
  
  const benchmarkData: BenchmarkData[] = [
    {
      yourValue: cogsRatio,
      industryAvg: benchmarks.cogs.avg,
      industryRange: { min: benchmarks.cogs.min, max: benchmarks.cogs.max },
      label: 'Cost of Goods Sold (COGS)',
      description: 'Direct costs attributable to the production of goods sold. Lower is generally better for profitability.'
    },
    {
      yourValue: grossMargin,
      industryAvg: benchmarks.grossMargin.avg,
      industryRange: { min: benchmarks.grossMargin.min, max: benchmarks.grossMargin.max },
      label: 'Gross Profit Margin',
      description: 'Revenue minus COGS as a percentage of revenue. Higher indicates better pricing power or efficiency.'
    },
    {
      yourValue: operatingExpenseRatio,
      industryAvg: benchmarks.operatingExpense.avg,
      industryRange: { min: benchmarks.operatingExpense.min, max: benchmarks.operatingExpense.max },
      label: 'Operating Expense Ratio',
      description: 'Operating expenses as a percentage of revenue. Lower indicates better operational efficiency.'
    },
    {
      yourValue: netProfitMargin,
      industryAvg: benchmarks.netProfit.avg,
      industryRange: { min: benchmarks.netProfit.min, max: benchmarks.netProfit.max },
      label: 'Net Profit Margin',
      description: 'Net income as a percentage of revenue. The bottom line measure of profitability.'
    }
  ];
  
  // Calculate overall alignment score
  const withinRangeCount = benchmarkData.filter(d => 
    d.yourValue >= d.industryRange.min && d.yourValue <= d.industryRange.max
  ).length;
  const alignmentScore = Math.round((withinRangeCount / benchmarkData.length) * 100);

  return (
    <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer transition-colors border-b"
        style={{ borderColor: 'var(--color-border-sub)' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Industry Benchmark Comparison</h3>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {businessType ? `${businessType} industry` : 'General industry'} averages from BLS data
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Alignment Score */}
            <div
              className="px-3 py-1 rounded-full text-sm font-medium"
              style={
                alignmentScore >= 75 ? { backgroundColor: 'transparent', color: 'var(--color-verified)' } :
                alignmentScore >= 50 ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-brand)' } :
                { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
              }
            >
              {alignmentScore}% aligned
            </div>
            <svg
              className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
              style={{ color: 'var(--color-text-dim)' }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Reconstructed Data Notice */}
          {hasReconstructedData && (
            <div className="border rounded-lg p-3 text-sm" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                <strong>Note:</strong> Financial data was reconstructed from bank/credit card statements.
                Industry benchmarks help validate the accuracy of reconstructed figures.
              </p>
            </div>
          )}
          
          {/* Benchmark Bars */}
          <div className="space-y-3">
            {benchmarkData.map((data, i) => (
              <BenchmarkBar key={i} data={data} />
            ))}
          </div>
          
          {/* Legend */}
          <div className="flex items-center gap-6 pt-4 border-t text-xs" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }} />
              <span>Industry range</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-0.5 h-3" style={{ backgroundColor: 'var(--color-brand)' }} />
              <span>Industry average</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-verified)' }} />
              <span>Your value (in range)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-brand)' }} />
              <span>Your value (out of range)</span>
            </div>
          </div>

          {/* Source Attribution */}
          <p className="text-xs text-center pt-2" style={{ color: 'var(--color-text-dim)' }}>
            Industry benchmarks derived from Bureau of Labor Statistics (BLS) and NAICS sector data.
            {naicsCode && ` NAICS Code: ${naicsCode}`}
          </p>
        </div>
      )}
    </div>
  );
}
